import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Plus,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
  LogOut,
  Shield,
  Key,
  MessageSquare,
  Bot,
  ThumbsUp,
  ThumbsDown,
  StopCircle,
  Copy,
  Check,
  ArrowDown,
  Search,
  Sparkles,
  Star,
  Pencil,
  RotateCcw,
  MessageSquarePlus,
  GraduationCap,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChangePassword from './ChangePassword';
import FeedbackModal from './FeedbackModal';

// Same Light highlighter as before, imported directly: the package index pulls
// in every highlight.js language (~1.7 MB) that Light never registers.
const SyntaxHighlighter = lazy(() => import('react-syntax-highlighter/dist/esm/light'));

const MAX_INPUT_CHARS = 2000;

let _msgId = 0;
const nextMsgId = () => `msg-${++_msgId}-${Date.now()}`;

/* ── Copy-to-clipboard helper ────────────────────────── */
const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  // Fallback for non-HTTPS contexts
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try { document.execCommand('copy'); } finally { document.body.removeChild(textarea); }
};

const CopyButton = ({ text, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await copyToClipboard(text);
      setCopied(true);
      toast.success('Copied to clipboard', { duration: 1500, style: { fontSize: '13px', padding: '8px 12px' } });
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Failed to copy'); }
  };
  return (
    <button onClick={handleCopy} aria-label={copied ? 'Copied' : 'Copy'} className={`copy-btn ${className}`}>
      {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
    </button>
  );
};

/* ── Memoized single message bubble ──────────────────── */
const MessageBubble = React.memo(({ message, index, thinkingStage, expandedSources, onToggleSources, onFeedback, onGiveFeedback, onSourceClick, onRegenerate, onEdit, isLastAssistant, isEditing, onStartEdit, onCancelEdit }) => {
  const isUser = message.type === 'user';
  const editTextareaRef = useRef(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`message-row ${isUser ? 'message-row--user' : 'message-row--assistant'}`}
    >
      {/* Avatar */}
      {!isUser && (
        <div className="msg-avatar" aria-hidden="true">
          <Bot size={16} />
        </div>
      )}

      <div className={`msg-bubble-wrapper ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Bubble */}
        <div className={`msg-bubble ${isUser ? 'msg-bubble--user' : 'msg-bubble--assistant'}`}>
          {message.streaming && !message.content ? (
            <div className="thinking-animation" role="status" aria-label="Assistant is thinking">
              <div className="thinking-indicator">
                <Sparkles size={14} className="thinking-icon" />
                <span className="thinking-text">{thinkingStage || 'Thinking...'}</span>
              </div>
              <div className="thinking-dots" aria-hidden="true">
                <div className="thinking-dot"></div>
                <div className="thinking-dot"></div>
                <div className="thinking-dot"></div>
              </div>
            </div>
          ) : isUser ? (
            isEditing ? (
              <div className="edit-message-form">
                <textarea
                  className="edit-message-textarea"
                  defaultValue={message.content}
                  ref={editTextareaRef}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const val = editTextareaRef.current?.value?.trim();
                      if (val) onEdit(index, val);
                    }
                    if (e.key === 'Escape') onCancelEdit();
                  }}
                />
                <div className="edit-message-actions">
                  <button onClick={() => { const val = editTextareaRef.current?.value?.trim(); if (val) onEdit(index, val); }} className="edit-save-btn">Save & Send</button>
                  <button onClick={onCancelEdit} className="edit-cancel-btn">Cancel</button>
                </div>
              </div>
            ) : (
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
            )
          ) : (
            <div className="prose prose-sm max-w-none prose-p:text-gray-700 prose-p:leading-relaxed prose-headings:text-gray-800 prose-strong:text-gray-900 prose-code:text-red-600 prose-code:bg-gray-100 prose-a:text-blue-600">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <div className="code-block-wrapper my-3">
                        <div className="code-block-header">
                          <span>{match[1]}</span>
                          <CopyButton text={String(children).replace(/\n$/, '')} className="code-copy-btn" />
                        </div>
                        <Suspense fallback={<pre className="p-3 bg-gray-900 text-gray-100 text-sm overflow-x-auto"><code>{children}</code></pre>}>
                          <SyntaxHighlighter
                            language={match[1]}
                            PreTag="div"
                            customStyle={{ margin: 0, fontSize: '0.8125rem', borderRadius: 0 }}
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        </Suspense>
                      </div>
                    ) : (
                      <code className="bg-gray-100 text-red-600 px-1 py-0.5 rounded text-[0.8em] font-mono" {...props}>{children}</code>
                    );
                  },
                  h2: ({ children }) => <h2 className="text-base font-semibold text-gray-800 mt-5 mb-2 border-b border-gray-200 pb-1">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-800 mt-4 mb-1.5">{children}</h3>,
                  h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-700 mt-3 mb-1">{children}</h4>,
                  blockquote: ({ children }) => <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-4 py-2 my-3 text-blue-900 rounded-r">{children}</blockquote>,
                  ul: ({ children }) => <ul className="list-disc ml-5 space-y-1 my-2 text-gray-700">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal ml-5 space-y-1 my-2 text-gray-700">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  p: ({ children }) => <p className="text-gray-700 leading-relaxed mb-2">{children}</p>,
                  a: ({ href, children }) => {
                    // Prevent javascript: and data: URL injection (XSS)
                    let safeHref = '#';
                    try {
                      const protocol = new URL(href, window.location.origin).protocol;
                      if (['http:', 'https:', 'mailto:'].includes(protocol)) safeHref = href;
                    } catch { /* invalid URL — keep # */ }
                    return <a href={safeHref} className="text-blue-600 underline hover:text-blue-800 transition-colors" target="_blank" rel="noopener noreferrer">{children}</a>;
                  },
                  table: ({ children }) => <div className="overflow-x-auto my-3"><table className="min-w-full border border-gray-200 rounded text-sm">{children}</table></div>,
                  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
                  th: ({ children }) => <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-800">{children}</th>,
                  td: ({ children }) => <td className="border border-gray-200 px-3 py-2 text-gray-700">{children}</td>,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Timestamp + Feedback + Copy */}
        {!(message.streaming && !message.content) && (
          <div className="msg-meta">
            <time className="text-[10px] text-gray-400" dateTime={message.timestamp}>
              {message.timestamp ? new Date(message.timestamp).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}
            </time>

            {/* Copy message */}
            {message.content && !message.streaming && (
              <CopyButton text={message.content} className="msg-copy-btn" />
            )}

            {/* Edit user message */}
            {isUser && !message.streaming && onStartEdit && (
              <button onClick={() => onStartEdit(index)} aria-label="Edit message" className="msg-action-btn" title="Edit">
                <Pencil size={12} />
              </button>
            )}

            {/* Feedback + Regenerate buttons for assistant messages */}
            {!isUser && message.messageId && !message.streaming && (
              <div className="feedback-buttons">
                <button
                  onClick={() => onFeedback(index, 'up')}
                  aria-label="Helpful"
                  className={`feedback-btn ${message.feedback === 'up' ? 'feedback-btn--active-up' : ''}`}
                >
                  <ThumbsUp size={14} />
                </button>
                <button
                  onClick={() => onFeedback(index, 'down')}
                  aria-label="Not helpful"
                  className={`feedback-btn ${message.feedback === 'down' ? 'feedback-btn--active-down' : ''}`}
                >
                  <ThumbsDown size={14} />
                </button>
                <button
                  onClick={() => onGiveFeedback(index)}
                  aria-label="Give feedback on this answer"
                  title="Give feedback on this answer"
                  className="feedback-btn feedback-btn--label"
                >
                  <MessageSquarePlus size={14} aria-hidden="true" />
                  <span>Feedback</span>
                </button>
                {isLastAssistant && message.content && (
                  <button
                    onClick={() => onRegenerate(index)}
                    aria-label="Regenerate response"
                    className="feedback-btn"
                    title="Regenerate"
                  >
                    <RotateCcw size={14} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Sources */}
        {message.sources && message.sources.length > 0 && (
          <div className="mt-1.5 w-full">
            <button
              onClick={() => onToggleSources(index)}
              aria-expanded={!!expandedSources[index]}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1"
            >
              {expandedSources[index] ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
              {expandedSources[index] ? 'Hide' : 'View'} {message.sources.length} source{message.sources.length > 1 ? 's' : ''}
            </button>
            <AnimatePresence>
              {expandedSources[index] && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2 p-3 bg-blue-50 rounded-xl border border-blue-100"
                >
                  <p className="text-xs font-semibold text-blue-700 mb-2">Referenced Sources</p>
                  {message.sources.map((source, idx) => {
                    const isAvailable = typeof source === 'object' ? source.available : true;
                    const docName = typeof source === 'object' ? source.document : source;
                    const page = typeof source === 'object' ? source.page : null;
                    const preview = typeof source === 'object' ? source.preview : null;
                    return (
                      <div
                        key={idx}
                        role={isAvailable ? 'button' : undefined}
                        tabIndex={isAvailable ? 0 : undefined}
                        onKeyDown={(e) => isAvailable && e.key === 'Enter' && onSourceClick(source)}
                        className={`source-item ${isAvailable ? 'source-item--clickable' : 'opacity-60'}`}
                        onClick={() => isAvailable && onSourceClick(source)}
                        title={isAvailable ? 'Click to open document' : 'Document not available'}
                      >
                        <span className="source-dot" aria-hidden="true">•</span>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-blue-800 truncate">
                            {docName}{page && page !== 'Unknown' ? ` — page:${page}` : ''}
                            {isAvailable && <span className="ml-1 text-blue-400">↗</span>}
                          </p>
                          {preview && (
                            <p className="text-[10px] text-blue-600 mt-0.5 italic line-clamp-2">"{preview}"</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
});

const Chat = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  const [thinkingStage, setThinkingStage] = useState('');
  const [expandedSources, setExpandedSources] = useState({});
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(null); // null = closed
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingMsgIndex, setEditingMsgIndex] = useState(null);
  const [loadingConversationId, setLoadingConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const isAdmin = currentUser?.role === 'admin';

  const welcomeMessage = {
    _id: nextMsgId(),
    type: 'assistant',
    content: "Hello! I'm CCITR IntelliBot, your AI assistant. How can I help you today?",
    timestamp: new Date().toISOString(),
    sources: []
  };

  useEffect(() => {
    loadConversations();
    setMessages([welcomeMessage]);
    // Keep sidebar open when crossing the lg breakpoint
    const mql = window.matchMedia('(min-width: 1024px)');
    const onBreakpoint = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setSidebarOpen(true);
    };
    mql.addEventListener('change', onBreakpoint);
    return () => mql.removeEventListener('change', onBreakpoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /* Detect if user scrolled away from bottom */
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(distFromBottom > 200);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  /* Keyboard shortcuts: Ctrl+N = new chat, Esc = close sidebar (mobile) */
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        startNewConversation();
      }
      if (e.key === 'Escape' && sidebarOpen && !isDesktop) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen, isDesktop]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /* Filtered conversations for search */
  const filteredConversations = useMemo(() => {
    let list = Array.isArray(conversations) ? conversations : [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c => c.title?.toLowerCase().includes(q));
    }
    // Pinned conversations always on top (API already sorts, but reinforce client-side)
    return [...list].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  }, [conversations, searchQuery]);

  const loadConversations = async () => {
    try {
      const response = await axios.get('/api/conversations');
      const conversationsData = response.data.conversations || response.data;
      setConversations(Array.isArray(conversationsData) ? conversationsData : []);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    }
  };

  const loadConversation = async (conversationId) => {
    setLoadingConversationId(conversationId);
    try {
      const response = await axios.get(`/api/conversations/${conversationId}`);
      const conversationHistory = response.data.messages || response.data.history || response.data;
      
      const formattedMessages = [welcomeMessage];
      if (conversationHistory && Array.isArray(conversationHistory)) {
        conversationHistory.forEach(msg => {
          if (msg.role === 'user') {
            formattedMessages.push({
              _id: nextMsgId(),
              type: 'user',
              content: msg.content,
              timestamp: msg.timestamp,
              messageId: msg.id || null,
            });
          } else if (msg.role === 'assistant') {
            let sources = msg.sources || [];
            if (!Array.isArray(sources)) {
              sources = [];
            }
            
            formattedMessages.push({
              _id: nextMsgId(),
              type: 'assistant',
              content: msg.content,
              sources: sources,
              timestamp: msg.timestamp,
              messageId: msg.id || null,
              feedback: msg.feedback || null,
            });
          }
        });
      }
      
      setMessages(formattedMessages);
      setCurrentConversationId(conversationId);
      setLoadingConversationId(null);
      if (!isDesktop) setSidebarOpen(false);
    } catch (error) {
      console.error('Error loading conversation:', error);
      toast.error('Failed to load conversation');
      setLoadingConversationId(null);
    }
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/conversations/${conversationId}`);
      loadConversations();
      if (currentConversationId === conversationId) {
        startNewConversation();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const handlePinConv = async (convId, e) => {
    e.stopPropagation();
    try {
      await axios.patch(`/api/conversations/${convId}/pin`);
      loadConversations();
    } catch (error) {
      toast.error('Failed to pin conversation');
    }
  };

  const handleRenameConv = async (convId) => {
    const newTitle = editingTitle.trim();
    if (!newTitle) { setEditingConvId(null); return; }
    try {
      await axios.patch(`/api/conversations/${convId}`, null, { params: { title: newTitle } });
      setEditingConvId(null);
      setEditingTitle('');
      loadConversations();
    } catch (error) {
      toast.error('Failed to rename conversation');
    }
  };

  const startNewConversation = async () => {
    try {
      const response = await axios.post('/api/new-conversation');
      const newConversationId = response.data.conversation_id;
      setCurrentConversationId(newConversationId);
      setMessages([welcomeMessage]);
      loadConversations();
      if (!isDesktop) setSidebarOpen(false);
    } catch (error) {
      console.error('Error starting new conversation:', error);
      toast.error('Failed to start new conversation');
    }
  };

  const handleRegenerate = (assistantIndex) => {
    if (isLoading) return;
    let userMsgText = null;
    for (let i = assistantIndex - 1; i >= 0; i--) {
      if (messages[i].type === 'user') { userMsgText = messages[i].content; break; }
    }
    if (!userMsgText) return;
    setMessages(prev => {
      const updated = prev.slice(0, assistantIndex);
      return [...updated, { _id: nextMsgId(), type: 'assistant', content: '', sources: [], streaming: true, timestamp: new Date().toISOString(), messageId: null, feedback: null }];
    });
    setIsLoading(true);
    setThinkingStage('Regenerating response...');
    sendStreamingRequest(userMsgText);
  };

  const handleEditMessage = (msgIndex, newContent) => {
    if (isLoading) return;
    setEditingMsgIndex(null);
    setMessages(prev => {
      const updated = prev.slice(0, msgIndex);
      updated.push({ ...prev[msgIndex], content: newContent, _id: nextMsgId() });
      return [...updated, { _id: nextMsgId(), type: 'assistant', content: '', sources: [], streaming: true, timestamp: new Date().toISOString(), messageId: null, feedback: null }];
    });
    setIsLoading(true);
    setThinkingStage('Processing your question...');
    sendStreamingRequest(newContent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      _id: nextMsgId(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    // Add a streaming placeholder for the assistant response
    const streamingMessage = {
      _id: nextMsgId(),
      type: 'assistant',
      content: '',
      sources: [],
      streaming: true,
      timestamp: new Date().toISOString(),
      messageId: null,
      feedback: null,
    };
    setMessages(prev => [...prev, streamingMessage]);
    setThinkingStage('Processing your question...');

    sendStreamingRequest(messageText);
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const toggleSources = (messageIndex) => {
    setExpandedSources(prev => ({
      ...prev,
      [messageIndex]: !prev[messageIndex]
    }));
  };

  // Feedback form for one answer, with the question it answered for context.
  const openAnswerFeedback = (messageIndex) => {
    const asked = messages.slice(0, messageIndex).reverse().find((m) => m.type === 'user');
    setFeedbackModal({ messageId: messages[messageIndex].messageId, question: asked?.content || '' });
  };

  const handleFeedback = async (messageIndex, rating) => {
    const msg = messages[messageIndex];
    if (!msg || !msg.messageId) return;

    // Toggle: clicking the same rating removes it
    const newRating = msg.feedback === rating ? null : rating;
    const prevFeedback = msg.feedback;

    // Optimistic update
    setMessages(prev => {
      const updated = [...prev];
      updated[messageIndex] = { ...updated[messageIndex], feedback: newRating };
      return updated;
    });

    try {
      if (newRating) {
        await axios.put(`/api/messages/${msg.messageId}/feedback`, { rating: newRating });
      } else {
        await axios.delete(`/api/messages/${msg.messageId}/feedback`);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Revert on error
      setMessages(prev => {
        const updated = [...prev];
        updated[messageIndex] = { ...updated[messageIndex], feedback: prevFeedback };
        return updated;
      });
    }
  };

  const handleSourceClick = async (source) => {
    const filename = typeof source === 'string' ? source : (source.source_file || source.document);
    const pageNumber = typeof source === 'object' ? source.page : null;
    const displayName = typeof source === 'object' ? source.document : source;

    const toastId = toast.loading(`Loading ${displayName}...`);
    try {
      // Fetch via axios so the JWT Authorization header is included
      const response = await axios.get(`/api/documents/${encodeURIComponent(filename)}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);

      // Append page number as a fragment so the browser jumps to the right page
      const urlWithPage =
        pageNumber && pageNumber !== 'Unknown'
          ? `${blobUrl}#page=${pageNumber}`
          : blobUrl;

      const tab = window.open(urlWithPage, '_blank');
      if (!tab) {
        toast.error('Popup blocked — please allow popups for this site.', { id: toastId });
      } else {
        toast.success(
          `Opened: ${displayName}${pageNumber && pageNumber !== 'Unknown' ? ` (Page ${pageNumber})` : ''}`,
          { id: toastId }
        );
        // Revoke the blob URL after a delay so the browser has time to load it
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(`Document not found: ${displayName}`, { id: toastId });
      } else {
        toast.error(`Failed to open document: ${displayName}`, { id: toastId });
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  // Extracted streaming logic
  const sendStreamingRequest = async (messageText) => {
    let timeoutId;

    try {
      const token = localStorage.getItem('token');
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Timeout after 5 minutes to prevent indefinite hangs
      timeoutId = setTimeout(() => controller.abort(), 300000);

      const response = await fetch('/api/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: messageText,
          conversation_id: currentConversationId,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let firstChunkReceived = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.trim()) continue;

          let eventType = '';
          let eventData = '';

          for (const line of part.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7);
            if (line.startsWith('data: ')) eventData = line.slice(6);
          }

          if (!eventType || !eventData) continue;

          let data;
          try { data = JSON.parse(eventData); } catch { continue; }

          switch (eventType) {
            case 'status':
              setThinkingStage(data.message || '');
              break;

            case 'chunk':
              if (!firstChunkReceived) {
                firstChunkReceived = true;
                setThinkingStage('');
              }
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.streaming) {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + (data.text || ''),
                  };
                }
                return updated;
              });
              break;

            case 'sources':
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.streaming) {
                  updated[updated.length - 1] = {
                    ...last,
                    sources: Array.isArray(data.sources) ? data.sources : [],
                  };
                }
                return updated;
              });
              break;

            case 'done':
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.streaming) {
                  updated[updated.length - 1] = {
                    ...last,
                    content: data.full_response || last.content,
                    sources: data.sources || last.sources,
                  };
                }
                return updated;
              });
              break;

            case 'message_saved':
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.streaming) {
                  updated[updated.length - 1] = {
                    ...last,
                    streaming: false,
                    messageId: data.message_id || null,
                  };
                }
                return updated;
              });
              if (data.conversation_id && data.conversation_id !== currentConversationId) {
                setCurrentConversationId(data.conversation_id);
              }
              loadConversations();
              break;

            case 'error':
              setMessages(prev => prev.filter(msg => msg.streaming !== true));
              toast.error(data.message || 'An error occurred');
              break;

            default:
              break;
          }
        }
      }

      if (timeoutId) clearTimeout(timeoutId);

      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && last.streaming) {
          updated[updated.length - 1] = { ...last, streaming: false };
        }
        return updated;
      });

    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.streaming) {
            updated[updated.length - 1] = {
              ...last,
              streaming: false,
              content: last.content || '*(Generation stopped)*',
            };
          }
          return updated;
        });
      } else {
        console.error('Error sending message:', error);
        setMessages(prev => prev.filter(msg => msg.streaming !== true));
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
      setThinkingStage('');
    }
  };

  // auto-grow textarea (debounced with rAF)
  const rafRef = useRef(null);
  const autoGrow = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    });
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    /* â”€â”€â”€ full-viewport shell â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
    <div className="chat-shell">

      {/* â”€â”€ SIDEBAR BACKDROP (mobile) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <AnimatePresence>
        {sidebarOpen && !isDesktop && (
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* â”€â”€ SIDEBAR â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <motion.aside
        role="navigation"
        aria-label="Conversations sidebar"
        aria-hidden={!sidebarOpen}
        initial={false}
        animate={
          isDesktop
            ? { width: sidebarOpen ? 260 : 0 }
            : { x: sidebarOpen ? 0 : -300 }
        }
        transition={{ type: 'spring', stiffness: 300, damping: 32, restDelta: 0.5 }}
        style={{ pointerEvents: sidebarOpen ? 'auto' : 'none' }}
        className="sidebar"
      >
            {/* Sidebar header */}
            <div className="sidebar-header">
              <div className="flex items-center gap-3 mb-3">
                <MessageSquare size={18} className="text-blue-300" aria-hidden="true" />
                <h2 className="text-base font-semibold tracking-wide">Conversations</h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close sidebar"
                  className="ml-auto p-1.5 rounded-lg hover:bg-white/10 transition-colors focus-visible:ring-2 focus-visible:ring-white/60 lg:hidden"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              <p className="text-xs text-blue-200 truncate mb-3">{currentUser?.email}</p>
              <button
                onClick={startNewConversation}
                className="sidebar-new-btn"
              >
                <Plus size={15} aria-hidden="true" />
                New Conversation
              </button>

              {/* Conversation search */}
              <div className="sidebar-search">
                <Search size={14} className="sidebar-search-icon" aria-hidden="true" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="sidebar-search-input"
                  aria-label="Search conversations"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="sidebar-search-clear" aria-label="Clear search">
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Conversation list */}
            <div className="sidebar-conv-list" role="list">
              {Array.isArray(conversations) && conversations.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-6 px-4">No conversations yet. Start one above.</p>
              )}
              {!Array.isArray(conversations) && (
                <p className="text-xs text-slate-400 text-center py-6 px-4">Loading...</p>
              )}
              {Array.isArray(filteredConversations) && filteredConversations.length === 0 && searchQuery && (
                <p className="text-xs text-slate-400 text-center py-6 px-4">No matching conversations</p>
              )}
              {Array.isArray(filteredConversations) && filteredConversations.map((conv) => (
                <motion.div
                  key={conv.id}
                  role="listitem"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`conv-item group ${currentConversationId === conv.id ? 'conv-item--active' : ''}`}
                  onClick={() => loadConversation(conv.id)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && loadConversation(conv.id)}
                  aria-current={currentConversationId === conv.id ? 'page' : undefined}
                >
                  {conv.pinned && <Star size={12} className="text-amber-400 fill-amber-400 shrink-0 mt-0.5" aria-label="Pinned" />}
                  <div className="flex-1 min-w-0">
                    {editingConvId === conv.id ? (
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onBlur={() => handleRenameConv(conv.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameConv(conv.id); if (e.key === 'Escape') setEditingConvId(null); e.stopPropagation(); }}
                        onClick={(e) => e.stopPropagation()}
                        className="conv-rename-input"
                        autoFocus
                        maxLength={100}
                      />
                    ) : (
                      <p className="text-sm font-medium truncate">{conv.title}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(conv.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="conv-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingConvId(conv.id); setEditingTitle(conv.title || ''); }}
                      aria-label={`Rename conversation: ${conv.title}`}
                      className="conv-action-btn"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={(e) => handlePinConv(conv.id, e)}
                      aria-label={conv.pinned ? 'Unpin conversation' : 'Pin conversation'}
                      className={`conv-action-btn ${conv.pinned ? 'conv-action-btn--pinned' : ''}`}
                    >
                      <Star size={12} />
                    </button>
                    <button
                      onClick={(e) => deleteConversation(conv.id, e)}
                      aria-label={`Delete conversation: ${conv.title}`}
                      className="conv-action-btn conv-action-btn--delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Sidebar footer */}
            <div className="sidebar-footer">
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="sidebar-footer-btn text-blue-300 hover:text-blue-100 hover:bg-blue-800/40"
                >
                  <Shield size={15} aria-hidden="true" />
                  Admin Panel
                </button>
              )}
              <button
                onClick={() => navigate('/training')}
                className="sidebar-footer-btn text-slate-300 hover:text-white hover:bg-white/10"
              >
                <GraduationCap size={15} aria-hidden="true" />
                CCITR Training
              </button>
              <button
                onClick={() => setShowChangePassword(true)}
                className="sidebar-footer-btn text-slate-300 hover:text-white hover:bg-white/10"
              >
                <Key size={15} aria-hidden="true" />
                Change Password
              </button>
              <button
                onClick={handleLogout}
                className="sidebar-footer-btn text-slate-300 hover:text-red-300 hover:bg-red-900/30"
              >
                <LogOut size={15} aria-hidden="true" />
                Sign Out
              </button>
            </div>
          </motion.aside>

      {/* — MAIN CONTENT ——————————————————————————————————————— */}
      <div className="chat-main">

        {/* Top header bar */}
        <header className="chat-header" role="banner">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={sidebarOpen}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors focus-visible:ring-2 focus-visible:ring-white/60 shrink-0"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={sidebarOpen ? 'close' : 'open'}
                  initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
                  transition={{ duration: 0.18, ease: 'easeInOut' }}
                  style={{ display: 'flex' }}
                >
                  {sidebarOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
                </motion.span>
              </AnimatePresence>
            </button>
            <img src="/ccitr-logo.png" alt="CCITR logo" className="h-7 sm:h-9 w-auto shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold leading-tight">CCITR IntelliBot</h1>
              <p className="text-[10px] sm:text-xs text-blue-200/80 leading-tight truncate italic">
                Beta — responses may contain inaccuracies. Verify important information with the source.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setFeedbackModal({})}
            aria-label="Give feedback"
            title="Give feedback"
            className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm text-blue-100 hover:bg-white/20 hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <MessageSquarePlus size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Feedback</span>
          </button>

          {/* User chip */}
          <div className="hidden sm:flex items-center gap-2 shrink-0 bg-white/10 rounded-full px-3 py-1.5">
            <div className="w-6 h-6 rounded-full bg-blue-400 flex items-center justify-center text-xs font-bold uppercase select-none" aria-hidden="true">
              {(currentUser?.full_name || currentUser?.designation || currentUser?.username || currentUser?.email || 'U')[0]}
            </div>
            <span className="text-sm text-blue-100 max-w-[120px] truncate">{currentUser?.full_name || currentUser?.designation || currentUser?.username || currentUser?.email}</span>
          </div>
          </div>
        </header>

        {/* â”€â”€ MESSAGES AREA â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <main
          id="chat-messages"
          ref={chatContainerRef}
          className="chat-messages custom-scrollbar"
          aria-label="Chat messages"
          aria-live="polite"
          aria-atomic="false"
        >
          {loadingConversationId ? (
            <div className="skeleton-messages">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className={`skeleton-row ${i % 2 === 0 ? 'skeleton-row--right' : ''}`}>
                  {i % 2 !== 0 && <div className="skeleton-avatar skeleton-shimmer" />}
                  <div className={`skeleton-bubble ${i % 2 === 0 ? 'skeleton-bubble--short' : ''} skeleton-shimmer`}>
                    <div className="skeleton-line skeleton-line--full" />
                    <div className="skeleton-line skeleton-line--medium" />
                    {i % 2 !== 0 && <div className="skeleton-line skeleton-line--short" />}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {messages.map((message, index) => {
                const isLastAssistant = message.type === 'assistant' && !message.streaming &&
                  !messages.slice(index + 1).some(m => m.type === 'assistant');
                return (
                  <MessageBubble
                    key={message._id || index}
                    message={message}
                    index={index}
                    thinkingStage={thinkingStage}
                    expandedSources={expandedSources}
                    onToggleSources={toggleSources}
                    onFeedback={handleFeedback}
                    onGiveFeedback={openAnswerFeedback}
                    onSourceClick={handleSourceClick}
                    onRegenerate={handleRegenerate}
                    onEdit={handleEditMessage}
                    isLastAssistant={isLastAssistant}
                    isEditing={editingMsgIndex === index}
                    onStartEdit={(idx) => setEditingMsgIndex(idx)}
                    onCancelEdit={() => setEditingMsgIndex(null)}
                  />
                );
              })}
            </AnimatePresence>
          )}

          <div ref={messagesEndRef} aria-hidden="true" />
        </main>

        {/* Scroll-to-bottom FAB */}
        <AnimatePresence>
          {showScrollBtn && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={scrollToBottom}
              aria-label="Scroll to bottom"
              className="scroll-to-bottom-btn"
            >
              <ArrowDown size={18} />
            </motion.button>
          )}
        </AnimatePresence>

        {/* â”€â”€ INPUT BAR â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="chat-input-bar">
          <form
            onSubmit={handleSubmit}
            className="chat-input-form"
            aria-label="Send a message"
          >
            <div className="chat-input-wrapper">
              <label htmlFor="chat-input" className="sr-only">Message</label>
              <textarea
                id="chat-input"
                ref={(el) => { inputRef.current = el; textareaRef.current = el; }}
                rows={1}
                value={inputMessage}
                onChange={(e) => { setInputMessage(e.target.value); autoGrow(); }}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything..."
                disabled={isLoading}
                maxLength={MAX_INPUT_CHARS}
                aria-label="Message input"
                aria-describedby="char-count"
                className="chat-input-textarea"
              />
              {inputMessage.length > MAX_INPUT_CHARS * 0.7 && (
                <div id="char-count" className="char-count" aria-live="polite">
                  <span className={inputMessage.length > MAX_INPUT_CHARS * 0.9 ? 'text-amber-500 font-medium' : ''}>
                    {inputMessage.length}/{MAX_INPUT_CHARS}
                  </span>
                </div>
              )}
            </div>
            {isLoading ? (
              <motion.button
                type="button"
                onClick={handleStop}
                aria-label="Stop generation"
                className="chat-send-btn chat-stop-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <StopCircle size={18} aria-hidden="true" />
              </motion.button>
            ) : (
              <motion.button
                type="submit"
                disabled={!inputMessage.trim()}
                aria-label="Send message"
                className="chat-send-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Send size={18} aria-hidden="true" />
              </motion.button>
            )}
          </form>
        </div>
      </div>

      {/* Change Password Modal */}
      {feedbackModal && <FeedbackModal {...feedbackModal} onClose={() => setFeedbackModal(null)} />}

      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default Chat;
